/**
 * App state (Zustand). The current cost sheet is autosaved to localStorage under the original key `ic2_sheet`,
 * so a sheet the AM had open in the single-file tool opens here unchanged.
 */
import { create } from "zustand";
import { blankSheet, DEFAULT_FX, NEUTRAL_QCOEF, hasSheet, type Lang, type Sheet } from "@ic/engine";

export type Cur = "USD" | "CNY";
import { KEYS, lsGet, lsSet } from "./shared/storage.ts";
import type { Meta, User } from "./shared/api.ts";

export type View = "ai" | "bom" | "sheet" | "admin" | "group";
export type ModalState =
  | { kind: "settings" }
  | { kind: "lookup"; line: "fab" | "trim"; i: number }
  | { kind: "lineai"; line: "fab" | "trim"; i: number }
  | { kind: "mysheets" }
  | { kind: "apply"; sc: string }
  | null;

/** Default quote coefficients: a base set and one per order quantity (JF 2026-10-13, on the server) */
export type { CoefPrefs } from "@ic/engine";
import type { CoefPrefs } from "@ic/engine";

interface State {
  /** my and the company's default coefficients, loaded after login */
  coefPrefs: { mine: CoefPrefs | null; company: CoefPrefs | null } | null;
  setCoefPrefs(p: { mine: CoefPrefs | null; company: CoefPrefs | null }): void;
  /** the currency amounts are shown and typed in (JF 2026-10-13); remembered in this browser */
  cur: Cur;
  setCur(c: Cur): void;
  /** company default RMB per USD (admin page); a sheet keeps its own in sheet.fx */
  defaultFx: number;
  setDefaultFx(fx: number): void;
  lang: Lang;
  view: View;
  sheet: Sheet;
  sheetId: string | null;
  meta: Meta | null;
  /** logged-in account (null = login screen) */
  user: User | null;
  setUser(u: User | null): void;
  modal: ModalState;
  toastMsg: string;
  toastSeq: number;
  /** side-by-side comparison with a historical style (JF 2026-09-29): open?, and which SC */
  compare: { on: boolean; sc: string | null };
  setCompare(c: { on: boolean; sc: string | null }): void;
  /** last partial re-pricing: the sheet before it (for undo) and a label */
  undo: { sheet: Sheet; label: string } | null;
  setUndo(u: { sheet: Sheet; label: string } | null): void;
  setLang(l: Lang): void;
  go(v: View): void;
  /** the cost sheet was opened from this style group: its page offers "back to the group" (JF 2026-10-13) */
  fromGroup: { id: string; name: string } | null;
  /** a new version was deployed since this page was opened — it must be refreshed (JF 2026-10-14) */
  stale: boolean;
  setStale(v: boolean): void;
  /** "my estimates" asked the AI page to continue this server job (JF 2026-10-16) */
  resumeJob: string | null;
  setResumeJob(id: string | null): void;
  setFromGroup(g: { id: string; name: string } | null): void;
  setSheet(s: Sheet, opts?: { id?: string | null }): void;
  /** fingerprint of the sheet as last saved / opened — "" = never saved (JF 2026-10-13: warn before losing changes) */
  savedFp: string;
  /** the current sheet counts as saved (after a save, or when a sheet is opened / created unchanged) */
  markSaved(s?: Sheet): void;
  /** true when the current sheet has changes that are not saved */
  isDirty(): boolean;
  editSheet(fn: (s: Sheet) => void): void;
  setMeta(m: Meta): void;
  openModal(m: ModalState): void;
  closeModal(): void;
  toast(msg: string): void;
}

function loadSheet(): Sheet {
  const sv = lsGet<Sheet | null>(KEYS.sheet, null);
  return sv && sv.h ? { ...blankSheet(), ...sv } : { ...blankSheet(undefined, { ...NEUTRAL_QCOEF }), quote: true };
}

/** Same fallback as the original: if the sheet (with its sketch image) does not fit, save it without the sketch. */
export function persistSheet(s: Sheet): void {
  try { localStorage.setItem(KEYS.sheet, JSON.stringify(s)); } catch {
    try { localStorage.setItem(KEYS.sheet, JSON.stringify({ ...s, sketch: "" })); } catch { /* give up */ }
  }
}

/** Scroll position per view (restored when coming back to a tab). */
export const scrollPos: Record<View, number> = { ai: 0, bom: lsGet<number>(KEYS.bomScroll, 0), sheet: 0, admin: 0, group: 0 };

/** What counts for "unsaved changes": the sheet's content, not UI state (the AI box open / closed). */
export const sheetFp = (s: Sheet): string => hash53(JSON.stringify({ ...s, ai: s.ai ? { ...s.ai, open: undefined } : null }));
/** A short hash (cyrb53), so the fingerprint stays small even with a sketch image in the sheet. */
function hash53(str: string): string {
  let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
  for (let i = 0; i < str.length; i++) { const c = str.charCodeAt(i); h1 = Math.imul(h1 ^ c, 2654435761); h2 = Math.imul(h2 ^ c, 1597334677); }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

export const useStore = create<State>((set, get) => ({
  lang: lsGet<string>(KEYS.lang, "zh") === "en" ? "en" : "zh",
  view: "ai",
  sheet: loadSheet(),
  sheetId: lsGet<string | null>(KEYS.sheetId, null),
  meta: null,
  user: null,
  setUser(u) { set({ user: u, ...(!u ? { toastMsg: "" } : {}) }); },
  modal: null,
  toastMsg: "",
  toastSeq: 0,
  undo: null,
  setUndo(u) { set({ undo: u }); },
  compare: { on: false, sc: null },
  setCompare(c) { set({ compare: c }); },
  setLang(l) { lsSet(KEYS.lang, l); set({ lang: l }); },
  go(v) {
    const cur = get().view;
    scrollPos[cur] = window.scrollY;
    if (cur === "bom") lsSet(KEYS.bomScroll, window.scrollY);
    set({ view: v });
    requestAnimationFrame(() => window.scrollTo(0, scrollPos[v] || 0));
  },
  coefPrefs: null,
  setCoefPrefs(p) { set({ coefPrefs: p }); },
  stale: false,
  setStale(v) { set({ stale: v }); },
  resumeJob: null,
  setResumeJob(id) { set({ resumeJob: id }); },
  fromGroup: null,
  setFromGroup(g) { set({ fromGroup: g }); },
  cur: lsGet<string>(KEYS.cur, "USD") === "CNY" ? "CNY" : "USD",
  setCur(c) { lsSet(KEYS.cur, c); set({ cur: c }); },
  defaultFx: DEFAULT_FX,
  setDefaultFx(fx) { if (fx > 0) set({ defaultFx: fx }); },
  savedFp: lsGet<string>(KEYS.savedFp, ""),
  markSaved(s) {
    const fp = sheetFp(s ?? get().sheet);
    try { localStorage.setItem(KEYS.savedFp, JSON.stringify(fp)); } catch { /* too big for storage: kept in memory only */ }
    set({ savedFp: fp });
  },
  isDirty() {
    const s = get().sheet;
    return hasSheet(s) && sheetFp(s) !== get().savedFp;
  },
  setSheet(s, opts) {
    persistSheet(s);
    const patch: Partial<State> = { sheet: s };
    if (opts && "id" in opts) { patch.sheetId = opts.id ?? null; lsSet(KEYS.sheetId, opts.id ?? null); }
    set(patch);
  },
  editSheet(fn) {
    const s = structuredClone(get().sheet);
    fn(s);
    persistSheet(s);
    set({ sheet: s });
  },
  setMeta(m) { set({ meta: m }); },
  openModal(m) { set({ modal: m }); },
  closeModal() { set({ modal: null }); },
  toast(msg) {
    if (/Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(msg)) { set({ stale: true }); msg = "系统刚更新过，请刷新页面（Ctrl+F5）后再试。/ The app was just updated — please refresh (Ctrl+F5)."; }
    set((st) => ({ toastMsg: msg, toastSeq: st.toastSeq + 1 })); },
}));

export const useHasSheet = () => useStore((s) => hasSheet(s.sheet));
